import React, { Component } from "react";
import classnames from "classnames";
import { format } from "date-fns";
import { observable } from "mobx";
import { observer } from "mobx-react";
import type { FormatterCallback } from "react-calendar";

import { Icon } from "@formant/ui/component/common/Icon";
import { animate } from "@formant/ui/utils/animate";

import { appStore } from "../../../stores";
import { Popup } from "../../common/Popup";
import { Switch } from "../../common/Switch";
import { Lazy } from "../../Lazy";
import * as styles from "./DateEdit.scss";

const Calendar = Lazy(async () => (await import("react-calendar")).default);

interface IDateEditProps {
    time: Date;
    maxTime?: Date | false;
    minTime?: Date | false;
    onDateChanged: (time: Date) => void;
    onClose: () => void;
}

@observer
export class DateEdit extends Component<IDateEditProps> {
    @observable
    private open: boolean = false;

    public async componentDidMount() {
        await animate();
        this.open = true;
    }

    public render() {
        const { open, onChangeDate, onClickOutside } = this;
        const { time, maxTime, minTime } = this.props;

        return (
            <Popup
                visible={open}
                className={classnames(styles["date-edit"], open && styles.open)}
                onClose={onClickOutside}
            >
                <Calendar
                    calendarType="US"
                    className={styles.calendar}
                    minDate={
                        minTime === undefined ? undefined : minTime || undefined
                    }
                    maxDate={
                        maxTime === undefined
                            ? new Date()
                            : maxTime || undefined
                    }
                    minDetail="month"
                    onChange={onChangeDate}
                    formatMonthYear={formantMonthYear}
                    formatShortWeekday={formatShortWeekday}
                    showNeighboringMonth={false}
                    prevLabel={<Icon name="previous" />}
                    nextLabel={<Icon name="next" />}
                    value={time}
                />
                <Switch
                    label="Use UTC time zone"
                    state={appStore.utc}
                    className={styles.utc}
                    onChange={this.onChangeUtc}
                />
            </Popup>
        );
    }

    private async close() {
        this.open = false;
        await animate();
        this.props.onClose();
    }

    private onChangeDate = async (date: Date | Date[]) => {
        if (date instanceof Array) {
            return;
        }
        this.props.onDateChanged(date);
        await this.close();
    };

    private onChangeUtc = (utc: boolean) => {
        appStore.utc = utc;
    };

    private onClickOutside = () => this.close();
}

const formantMonthYear: FormatterCallback = (_, date) => format(date, "MMMM");
const formatShortWeekday: FormatterCallback = (_, date) =>
    date.toLocaleDateString("en-US", { weekday: "narrow" });
